/**
 * Component Configuration Modal
 * Popup interface for configuring Learning Objectives, Knowledge Checks, and Key Takeaways
 */

import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Palette, Eye } from 'lucide-react';
import { extractKnowledgeCheck, extractLearningObjectives, extractExampleBox, extractCallout } from '@/utils/componentDetector';

export type ComponentType = 'learning-objectives' | 'knowledge-check' | 'key-takeaways' | 'example-box' | 'callout-tip' | 'callout-warning' | 'callout-note' | 'callout-info';

interface ComponentConfig {
  type: ComponentType;
  title: string;
  icon: string; // Customizable icon/emoji
  useCustomIcon?: boolean; // Whether to use custom icon or default bullets
  items?: string[]; // For objectives and takeaways
  question?: string; // For knowledge check
  answer?: string; // For knowledge check
  extension?: string; // For knowledge check "Going Deeper"
  content?: string; // For example-box and callouts
  // Font sizes
  fontSize?: {
    title: string;
    question: string;
    answer: string;
  };
  // Knowledge Check specific styling
  style?: {
    borderStyle: 'solid' | 'dashed' | 'dotted' | 'none';
    borderWidth: string;
    borderRadius: string;
    padding: string;
    shadow: 'none' | 'sm' | 'md' | 'lg';
    buttonStyle: 'rounded' | 'pill' | 'square';
    buttonText: string;
    showTitleIcon: boolean;
    showAnswerLabel: boolean;
    showExtensionLabel: boolean;
    answerBorderStyle: 'solid' | 'dashed' | 'gradient';
  };
  colors: {
    background: string;
    border: string;
    text: string;
    icon: string;
    buttonBg?: string;
    buttonText?: string;
    answerBg?: string;
  };
}

interface ComponentConfigModalProps {
  isOpen: boolean;
  type: ComponentType;
  onClose: () => void;
  onInsert: (html: string) => void;
  editingElement?: HTMLElement | null; // Element being edited (for pre-filling)
}

const DEFAULT_CONFIGS: Record<ComponentType, ComponentConfig> = {
  'learning-objectives': {
    type: 'learning-objectives',
    title: 'Learning Objectives',
    icon: '✅',
    useCustomIcon: false, // Use bullets by default
    items: [
      'First learning objective - edit this text',
      'Second learning objective - edit this text',
      'Third learning objective - edit this text',
    ],
    colors: {
      background: '#e0f2fe',
      border: '#3b82f6',
      text: '#1e293b',
      icon: '#10b981',
    },
  },
  'knowledge-check': {
    type: 'knowledge-check',
    title: 'Knowledge Check',
    icon: '🎯',
    question: 'Your question here?',
    answer: 'Your answer here.',
    extension: 'Additional insight here...',
    fontSize: {
      title: '1.1em',
      question: '1em',
      answer: '0.95em',
    },
    style: {
      borderStyle: 'solid',
      borderWidth: '1px',
      borderRadius: '8px',
      padding: '20px',
      shadow: 'sm',
      buttonStyle: 'rounded',
      buttonText: 'Show Answer',
      showTitleIcon: true,
      showAnswerLabel: true,
      showExtensionLabel: true,
      answerBorderStyle: 'solid',
    },
    colors: {
      background: '#ecfdf5',
      border: '#10b981',
      text: '#1e293b',
      icon: '#3b82f6',
      buttonBg: '#3b82f6',
      buttonText: '#ffffff',
      answerBg: '#ffffff',
    },
  },
  'key-takeaways': {
    type: 'key-takeaways',
    title: 'Key Takeaways',
    icon: '💡',
    useCustomIcon: true, // Use custom icons by default for takeaways
    items: [
      'First key point - edit this text',
      'Second key point - edit this text',
      'Third key point - edit this text',
    ],
    colors: {
      background: '#e6f7ff',
      border: '#3b82f6',
      text: '#1e293b',
      icon: '#10b981',
    },
  },
  'example-box': {
    type: 'example-box',
    title: 'Example',
    icon: '📝',
    content: 'Add your example content here. This could be a real-world scenario, code example, or practical demonstration that helps illustrate the concept being taught.',
    colors: {
      background: '#fef3c7',
      border: '#f59e0b',
      text: '#1e293b',
      icon: '#d97706',
    },
  },
  'callout-tip': {
    type: 'callout-tip',
    title: 'Pro Tip',
    icon: '💡',
    content: 'Add a helpful tip or best practice here that will help learners succeed.',
    colors: {
      background: '#ecfdf5',
      border: '#10b981',
      text: '#1e293b',
      icon: '#059669',
    },
  },
  'callout-warning': {
    type: 'callout-warning',
    title: 'Warning',
    icon: '⚠️',
    content: 'Add an important warning or caution that learners should be aware of.',
    colors: {
      background: '#fef2f2',
      border: '#ef4444',
      text: '#1e293b',
      icon: '#dc2626',
    },
  },
  'callout-note': {
    type: 'callout-note',
    title: 'Note',
    icon: '📌',
    content: 'Add an important note or reminder for learners to keep in mind.',
    colors: {
      background: '#f3f4f6',
      border: '#6b7280',
      text: '#1e293b',
      icon: '#4b5563',
    },
  },
  'callout-info': {
    type: 'callout-info',
    title: 'Did You Know?',
    icon: 'ℹ️',
    content: 'Add interesting facts, additional context, or supplementary information here.',
    colors: {
      background: '#eff6ff',
      border: '#3b82f6',
      text: '#1e293b',
      icon: '#2563eb',
    },
  },
};

const ICON_OPTIONS: Record<ComponentType, string[]> = {
  'learning-objectives': ['✅', '📚', '🎓', '📝', '✔️', '🎯', '📌', '⭐'],
  'knowledge-check': ['🎯', '❓', '🤔', '💭', '🧠', '❔', '✨', '🔍'],
  'key-takeaways': ['💡', '🔑', '⚡', '✨', '🌟', '💫', '🎁', '📍'],
  'example-box': ['📝', '💻', '🔬', '📊', '🎨', '🧪', '📋', '🔧'],
  'callout-tip': ['💡', '✨', '🌟', '⭐', '💫', '🚀', '👍', '🎯'],
  'callout-warning': ['⚠️', '🚨', '❗', '⛔', '🔴', '❌', '🚫', '⚡'],
  'callout-note': ['📌', '📝', '🗒️', '📋', '✏️', '🔖', '📎', '📍'],
  'callout-info': ['ℹ️', '💬', '🔵', '📘', '🧠', '🎓', '📖', '💭'],
};

const COMPONENT_INFO: Record<ComponentType, { title: string; icon: string; description: string }> = {
  'learning-objectives': {
    title: 'Configure Learning Objectives',
    icon: '✅',
    description: 'Blue box with checkmarks for listing learning goals',
  },
  'knowledge-check': {
    title: 'Configure Knowledge Check',
    icon: '🎯',
    description: 'Interactive Q&A box with show/hide answer',
  },
  'key-takeaways': {
    title: 'Configure Key Takeaways',
    icon: '💡',
    description: 'Summary box with lightbulb icons for key points',
  },
  'example-box': {
    title: 'Configure Example Box',
    icon: '📝',
    description: 'Highlighted box for examples and practical demonstrations',
  },
  'callout-tip': {
    title: 'Configure Tip Callout',
    icon: '💡',
    description: 'Green callout for tips and best practices',
  },
  'callout-warning': {
    title: 'Configure Warning Callout',
    icon: '⚠️',
    description: 'Red callout for warnings and cautions',
  },
  'callout-note': {
    title: 'Configure Note Callout',
    icon: '📌',
    description: 'Gray callout for notes and reminders',
  },
  'callout-info': {
    title: 'Configure Info Callout',
    icon: 'ℹ️',
    description: 'Blue callout for additional information',
  },
};

/**
 * Helper to convert RGB to Hex
 */
function rgbToHex(rgb: string): string {
  if (!rgb || rgb === 'transparent' || rgb === 'rgba(0, 0, 0, 0)') return '';
  if (rgb.startsWith('#')) return rgb.toLowerCase();

  // Handle rgb format
  const rgbMatch = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (rgbMatch) {
    const hex = (x: string) => parseInt(x).toString(16).padStart(2, '0');
    return `#${hex(rgbMatch[1])}${hex(rgbMatch[2])}${hex(rgbMatch[3])}`;
  }

  // Handle rgba format
  const rgbaMatch = rgb.match(/^rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)$/);
  if (rgbaMatch) {
    const hex = (x: string) => parseInt(x).toString(16).padStart(2, '0');
    return `#${hex(rgbaMatch[1])}${hex(rgbaMatch[2])}${hex(rgbaMatch[3])}`;
  }

  return rgb.toLowerCase();
}

/**
 * Parse inline style string to extract a specific property
 */
function getStyleProperty(styleString: string, property: string): string {
  const regex = new RegExp(`${property}\\s*:\\s*([^;]+)`, 'i');
  const match = styleString.match(regex);
  return match ? match[1].trim() : '';
}

/**
 * Extract border color from border shorthand (e.g., "5px solid #3b82f6")
 */
function extractBorderColor(borderValue: string): string {
  // Match hex color
  const hexMatch = borderValue.match(/#[0-9a-fA-F]{3,6}/);
  if (hexMatch) return hexMatch[0];

  // Match rgb/rgba
  const rgbMatch = borderValue.match(/rgba?\([^)]+\)/);
  if (rgbMatch) return rgbToHex(rgbMatch[0]);

  // Match color name (basic)
  const colorNames = ['red', 'blue', 'green', 'black', 'white', 'gray', 'grey'];
  for (const color of colorNames) {
    if (borderValue.toLowerCase().includes(color)) return color;
  }

  return '';
}

/**
 * Extract colors from element's computed/inline styles
 */
function extractColorsFromElement(element: HTMLElement, defaultColors: ComponentConfig['colors']): ComponentConfig['colors'] {
  const styleAttr = element.getAttribute('style') || '';
  const computedStyle = window.getComputedStyle(element);

  // Check for data attributes first (most reliable for our components)
  const dataTextColor = element.getAttribute('data-text-color');
  const dataIconColor = element.getAttribute('data-icon-color');

  // Extract background color
  let backgroundColor = getStyleProperty(styleAttr, 'background-color') ||
                        getStyleProperty(styleAttr, 'background') ||
                        rgbToHex(computedStyle.backgroundColor);
  backgroundColor = rgbToHex(backgroundColor);

  // Extract border color from border-left or border shorthand
  let borderColor = '';
  const borderLeft = getStyleProperty(styleAttr, 'border-left');
  const border = getStyleProperty(styleAttr, 'border');
  const borderColorDirect = getStyleProperty(styleAttr, 'border-color') || getStyleProperty(styleAttr, 'border-left-color');

  if (borderColorDirect) {
    borderColor = rgbToHex(borderColorDirect);
  } else if (borderLeft) {
    borderColor = extractBorderColor(borderLeft);
  } else if (border) {
    borderColor = extractBorderColor(border);
  } else {
    borderColor = rgbToHex(computedStyle.borderLeftColor || computedStyle.borderColor);
  }

  // Extract text color - first check data attribute, then inline styles
  let textColor = dataTextColor || '';
  if (!textColor) {
    const listItem = element.querySelector('li');
    const heading = element.querySelector('h3');

    if (listItem) {
      const liStyle = listItem.getAttribute('style') || '';
      textColor = getStyleProperty(liStyle, 'color');
      if (!textColor) {
        textColor = rgbToHex(window.getComputedStyle(listItem).color);
      }
    } else if (heading) {
      const hStyle = heading.getAttribute('style') || '';
      textColor = getStyleProperty(hStyle, 'color');
      if (!textColor) {
        textColor = rgbToHex(window.getComputedStyle(heading).color);
      }
    }
  }

  // If still no text color found, try the element itself
  if (!textColor) {
    textColor = getStyleProperty(styleAttr, 'color') || rgbToHex(computedStyle.color);
  }

  // Extract icon color - first check data attribute
  let iconColor = dataIconColor || '';
  if (!iconColor) {
    // Look for styled buttons (knowledge check) or try to find accent colors
    const button = element.querySelector('button[style*="background"]');
    const styledSpan = element.querySelector('span[style*="color"]');

    if (button) {
      const btnStyle = button.getAttribute('style') || '';
      const btnBg = getStyleProperty(btnStyle, 'background-color') || getStyleProperty(btnStyle, 'background');
      if (btnBg) iconColor = rgbToHex(btnBg);
    } else if (styledSpan) {
      const spanStyle = styledSpan.getAttribute('style') || '';
      const spanColor = getStyleProperty(spanStyle, 'color');
      if (spanColor) iconColor = rgbToHex(spanColor);
    }
  }

  return {
    background: backgroundColor || defaultColors.background,
    border: borderColor || defaultColors.border,
    text: textColor || defaultColors.text,
    icon: iconColor || defaultColors.icon,
  };
}

/**
 * Extract component data from existing HTML element
 */
function extractComponentData(element: HTMLElement, type: ComponentType): ComponentConfig {
  const defaultConfig = DEFAULT_CONFIGS[type];

  // Extract colors from the element
  const colors = extractColorsFromElement(element, defaultConfig.colors);

  if (type === 'knowledge-check') {
    const data = extractKnowledgeCheck(element);
    // Extract the icon from data attribute
    const iconAttr = element.getAttribute('data-icon');
    const icon = iconAttr || defaultConfig.icon;
    // Extract font sizes from data attributes
    const fontTitle = element.getAttribute('data-font-title') || '1.1em';
    const fontQuestion = element.getAttribute('data-font-question') || '1em';
    const fontAnswer = element.getAttribute('data-font-answer') || '0.95em';
    // Extract style attributes
    const style = {
      borderStyle: (element.getAttribute('data-border-style') as 'solid' | 'dashed' | 'dotted' | 'none') || 'solid',
      borderWidth: element.getAttribute('data-border-width') || '1px',
      borderRadius: element.getAttribute('data-border-radius') || '8px',
      padding: element.getAttribute('data-padding') || '20px',
      shadow: (element.getAttribute('data-shadow') as 'none' | 'sm' | 'md' | 'lg') || 'sm',
      buttonStyle: (element.getAttribute('data-button-style') as 'rounded' | 'pill' | 'square') || 'rounded',
      buttonText: element.getAttribute('data-button-text') || 'Show Answer',
      showTitleIcon: element.getAttribute('data-show-title-icon') !== 'false',
      showAnswerLabel: element.getAttribute('data-show-answer-label') !== 'false',
      showExtensionLabel: element.getAttribute('data-show-extension-label') !== 'false',
      answerBorderStyle: (element.getAttribute('data-answer-border-style') as 'solid' | 'dashed' | 'gradient') || 'solid',
    };
    // Extract additional colors
    const buttonBg = element.getAttribute('data-button-bg') || defaultConfig.colors.buttonBg || '#3b82f6';
    const buttonTextColor = element.getAttribute('data-button-text-color') || defaultConfig.colors.buttonText || '#ffffff';
    const answerBg = element.getAttribute('data-answer-bg') || defaultConfig.colors.answerBg || '#ffffff';
    return {
      ...defaultConfig,
      title: data.checkpointType || defaultConfig.title,
      question: data.question || defaultConfig.question || '',
      answer: data.answer || defaultConfig.answer || '',
      extension: data.extension || defaultConfig.extension || '',
      icon,
      fontSize: {
        title: fontTitle,
        question: fontQuestion,
        answer: fontAnswer,
      },
      style,
      colors: {
        ...colors,
        buttonBg,
        buttonText: buttonTextColor,
        answerBg,
      },
    };
  }

  if (type === 'learning-objectives' || type === 'key-takeaways') {
    const data = extractLearningObjectives(element);
    // Check if custom icon was used
    const useCustomIconAttr = element.getAttribute('data-use-custom-icon');
    const useCustomIcon = useCustomIconAttr === 'true';
    // Extract the icon from data attribute
    const iconAttr = element.getAttribute('data-icon');
    const icon = iconAttr || defaultConfig.icon;
    return {
      ...defaultConfig,
      title: data.title || defaultConfig.title,
      items: data.items.length > 0 ? data.items : defaultConfig.items || [],
      useCustomIcon,
      icon,
      colors,
    };
  }

  if (type === 'example-box') {
    const data = extractExampleBox(element);
    // Extract the icon from data attribute
    const iconAttr = element.getAttribute('data-icon');
    const icon = iconAttr || defaultConfig.icon;
    return {
      ...defaultConfig,
      title: data.title || defaultConfig.title,
      content: data.content || defaultConfig.content || '',
      icon,
      colors,
    };
  }

  if (type.startsWith('callout-')) {
    const data = extractCallout(element);
    // Extract the icon from data attribute
    const iconAttr = element.getAttribute('data-icon');
    const icon = iconAttr || defaultConfig.icon;
    return {
      ...defaultConfig,
      title: data.title || defaultConfig.title,
      content: data.content || defaultConfig.content || '',
      icon,
      colors,
    };
  }

  return { ...defaultConfig, colors };
}

export default function ComponentConfigModal({
  isOpen,
  type,
  onClose,
  onInsert,
  editingElement,
}: ComponentConfigModalProps) {
  const [config, setConfig] = useState<ComponentConfig>(() => {
    // If editing, extract data from element
    if (editingElement) {
      return extractComponentData(editingElement, type);
    }
    return DEFAULT_CONFIGS[type];
  });
  const [showPreview, setShowPreview] = useState(false);
  const [showAnswerPreview, setShowAnswerPreview] = useState(false);

  // Reset config when type changes or when starting to edit a different element
  useEffect(() => {
    if (editingElement) {
      setConfig(extractComponentData(editingElement, type));
    } else {
      setConfig(DEFAULT_CONFIGS[type]);
    }
  }, [editingElement, type]);

  if (!isOpen) return null;

  const info = COMPONENT_INFO[type];

  // Update title
  const handleTitleChange = (title: string) => {
    setConfig({ ...config, title });
  };

  // Update list items
  const handleItemChange = (index: number, value: string) => {
    if (!config.items) return;
    const newItems = [...config.items];
    newItems[index] = value;
    setConfig({ ...config, items: newItems });
  };

  const handleAddItem = () => {
    if (!config.items) return;
    setConfig({ ...config, items: [...config.items, 'New item - edit this'] });
  };

  const handleRemoveItem = (index: number) => {
    if (!config.items) return;
    const newItems = config.items.filter((_, i) => i !== index);
    setConfig({ ...config, items: newItems });
  };

  // Update colors
  const handleColorChange = (key: keyof ComponentConfig['colors'], value: string) => {
    setConfig({
      ...config,
      colors: { ...config.colors, [key]: value },
    });
  };

  // Generate HTML
  const generateHTML = (): string => {
    const timestamp = Date.now();
    // Generate unique ID for scoped styles
    const componentId = `component-${timestamp}-${Math.random().toString(36).substr(2, 9)}`;

    if (type === 'learning-objectives') {
      const useCustomIcon = config.useCustomIcon ?? false;
      const iconEmoji = config.icon;

      if (useCustomIcon) {
        // Custom icon mode - use emoji before each item
        const itemsHTML = config.items?.map((item) => `    <li style="margin-bottom: 5px; padding-left: 25px; position: relative; color: ${config.colors.text}; list-style: none !important;">${item}</li>`).join('\n') || '';
        return `<div id="${componentId}" class="learning-objectives skillzen-component" style="background-color: ${config.colors.background}; border-left: 5px solid ${config.colors.border}; padding: 15px 20px; margin-bottom: 25px; border-radius: 4px; position: relative;" data-component="learning-objectives" data-use-custom-icon="true" data-icon="${iconEmoji}" data-text-color="${config.colors.text}" data-icon-color="${config.colors.icon}">
  <button type="button" class="component-delete-btn" onclick="this.parentElement.remove()" title="Delete" style="position: absolute; top: -10px; right: -10px; width: 24px; height: 24px; background: #ef4444; border: 2px solid white; border-radius: 50%; cursor: pointer; display: none; align-items: center; justify-content: center; color: white; font-size: 14px; line-height: 1; z-index: 10; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">&times;</button>
  <h3 style="color: ${config.colors.border}; margin-top: 0; margin-bottom: 10px;">${config.title}</h3>
  <ul style="list-style: none !important; padding: 0 !important; margin: 0;">
${itemsHTML}
  </ul>
</div>
<style data-for="${componentId}">
  .editor-content-preview #${componentId}:hover .component-delete-btn { display: flex !important; }
  #${componentId} ul { list-style-type: none !important; padding-left: 0 !important; }
  #${componentId} ul li { list-style: none !important; }
  #${componentId} ul li:before {
    content: '${iconEmoji}';
    color: ${config.colors.icon};
    position: absolute;
    left: 0;
    font-weight: bold;
  }
</style>`;
      } else {
        // Bullet mode - use standard bullet points
        const itemsHTML = config.items?.map((item) => `    <li style="margin-bottom: 5px; color: ${config.colors.text};">${item}</li>`).join('\n') || '';
        return `<div id="${componentId}" class="learning-objectives skillzen-component" style="background-color: ${config.colors.background}; border-left: 5px solid ${config.colors.border}; padding: 15px 20px; margin-bottom: 25px; border-radius: 4px; position: relative;" data-component="learning-objectives" data-use-custom-icon="false" data-icon="${iconEmoji}" data-text-color="${config.colors.text}" data-icon-color="${config.colors.icon}">
  <button type="button" class="component-delete-btn" onclick="this.parentElement.remove()" title="Delete" style="position: absolute; top: -10px; right: -10px; width: 24px; height: 24px; background: #ef4444; border: 2px solid white; border-radius: 50%; cursor: pointer; display: none; align-items: center; justify-content: center; color: white; font-size: 14px; line-height: 1; z-index: 10; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">&times;</button>
  <h3 style="color: ${config.colors.border}; margin-top: 0; margin-bottom: 10px;">${config.title}</h3>
  <ul style="list-style-type: disc; padding-left: 20px; margin: 0; color: ${config.colors.icon};">
${itemsHTML}
  </ul>
</div>
<style data-for="${componentId}">
  .editor-content-preview #${componentId}:hover .component-delete-btn { display: flex !important; }
  #${componentId} ul li:before { content: none !important; }
</style>`;
      }
    }

    if (type === 'knowledge-check') {
      const answerId = `answer-${timestamp}`;
      const fontSize = config.fontSize || { title: '1.1em', question: '1em', answer: '0.95em' };
      const style = config.style || {
        borderStyle: 'solid',
        borderWidth: '1px',
        borderRadius: '8px',
        padding: '20px',
        shadow: 'sm',
        buttonStyle: 'rounded',
        buttonText: 'Show Answer',
        showAnswerLabel: true,
        showExtensionLabel: true,
        answerBorderStyle: 'solid',
      };
      const buttonBg = config.colors.buttonBg || config.colors.icon;
      const buttonTextColor = config.colors.buttonText || '#ffffff';
      const answerBg = config.colors.answerBg || '#ffffff';

      // Shadow styles
      const shadowStyles: Record<string, string> = {
        none: 'none',
        sm: '0 1px 2px rgba(0,0,0,0.05)',
        md: '0 4px 6px rgba(0,0,0,0.1)',
        lg: '0 10px 15px rgba(0,0,0,0.1)',
      };

      // Button border radius based on style
      const buttonRadius: Record<string, string> = {
        rounded: '6px',
        pill: '50px',
        square: '0px',
      };

      // Answer border style
      const answerBorderStyles: Record<string, string> = {
        solid: `4px solid ${config.colors.icon}`,
        dashed: `4px dashed ${config.colors.icon}`,
        gradient: `4px solid ${config.colors.icon}`,
      };

      const dataAttrs = `data-component="knowledge-check" data-icon="${config.icon}" data-font-title="${fontSize.title}" data-font-question="${fontSize.question}" data-font-answer="${fontSize.answer}" data-border-style="${style.borderStyle}" data-border-width="${style.borderWidth}" data-border-radius="${style.borderRadius}" data-padding="${style.padding}" data-shadow="${style.shadow}" data-button-style="${style.buttonStyle}" data-button-text="${style.buttonText}" data-show-title-icon="${style.showTitleIcon}" data-show-answer-label="${style.showAnswerLabel}" data-show-extension-label="${style.showExtensionLabel}" data-answer-border-style="${style.answerBorderStyle}" data-button-bg="${buttonBg}" data-button-text-color="${buttonTextColor}" data-answer-bg="${answerBg}"`;

      return `<div id="${componentId}" class="learning-checkpoint skillzen-component" style="background-color: ${config.colors.background}; border: ${style.borderWidth} ${style.borderStyle} ${config.colors.border}; padding: ${style.padding}; margin: 30px 0; border-radius: ${style.borderRadius}; position: relative; box-shadow: ${shadowStyles[style.shadow]};" ${dataAttrs}>
  <button type="button" class="component-delete-btn" onclick="this.parentElement.remove()" title="Delete" style="position: absolute; top: -10px; right: -10px; width: 24px; height: 24px; background: #ef4444; border: 2px solid white; border-radius: 50%; cursor: pointer; display: none; align-items: center; justify-content: center; color: white; font-size: 14px; line-height: 1; z-index: 10; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">&times;</button>
  <div class="checkpoint-type" style="font-weight: bold; color: ${config.colors.border}; margin-bottom: 12px; display: flex; align-items: center; font-size: ${fontSize.title};">
    ${style.showTitleIcon ? `<span style="margin-right: 10px; font-size: 1.3em;">${config.icon}</span>` : ''} ${config.title}
  </div>
  <div class="question" style="font-weight: 600; margin-bottom: 16px; color: ${config.colors.text}; font-size: ${fontSize.question}; line-height: 1.5;">${config.question}</div>
  <div class="answer" id="${answerId}" style="display: none; background-color: ${answerBg}; border-left: ${answerBorderStyles[style.answerBorderStyle]}; padding: 16px; margin-top: 12px; margin-bottom: 16px; border-radius: 6px; color: ${config.colors.text}; font-size: ${fontSize.answer}; line-height: 1.6; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
    ${style.showAnswerLabel ? `<strong style="color: ${config.colors.icon}; font-size: 0.9em; text-transform: uppercase; letter-spacing: 0.5px;">Answer:</strong>` : ''}
    <div style="margin-top: ${style.showAnswerLabel ? '8px' : '0'};">${config.answer}</div>
    ${config.extension ? `<div class="extension" style="margin-top: 14px; padding-top: 14px; border-top: 1px dashed #d1d5db; font-style: italic; color: #4b5563;">${style.showExtensionLabel ? `<span style="color: ${config.colors.icon}; font-weight: 600;">💡 Going Deeper:</span> ` : ''}${config.extension}</div>` : ''}
  </div>
  <button type="button" onclick="event.preventDefault(); event.stopPropagation(); (function(btn){ var ans=document.getElementById('${answerId}'); if(ans){ if(ans.style.display==='none'||ans.style.display===''){ ans.style.display='block'; btn.textContent='Hide Answer'; btn.style.backgroundColor='#dc3545'; } else { ans.style.display='none'; btn.textContent='${style.buttonText}'; btn.style.backgroundColor='${buttonBg}'; } } })(this); return false;" class="reveal-btn" style="background-color: ${buttonBg}; color: ${buttonTextColor}; border: none; padding: 12px 24px; border-radius: ${buttonRadius[style.buttonStyle]}; cursor: pointer; font-size: 0.95em; font-weight: 600; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">${style.buttonText}</button>
</div>
<style data-for="${componentId}">
  .editor-content-preview #${componentId}:hover .component-delete-btn { display: flex !important; }
  #${componentId} .reveal-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 6px rgba(0,0,0,0.15); }
</style>`;
    }

    if (type === 'key-takeaways') {
      const useCustomIcon = config.useCustomIcon ?? true;
      const iconEmoji = config.icon;

      if (useCustomIcon) {
        // Custom icon mode
        const itemsHTML = config.items?.map((item) => `    <li style="padding-left: 25px; position: relative; margin-bottom: 10px; color: ${config.colors.text}; list-style: none !important;">${item}</li>`).join('\n') || '';
        return `<div id="${componentId}" class="key-takeaways skillzen-component" style="background-color: ${config.colors.background}; border-left: 5px solid ${config.colors.border}; padding: 20px; margin-top: 40px; border-radius: 4px; position: relative;" data-component="key-takeaways" data-use-custom-icon="true" data-icon="${iconEmoji}" data-text-color="${config.colors.text}" data-icon-color="${config.colors.icon}">
  <button type="button" class="component-delete-btn" onclick="this.parentElement.remove()" title="Delete" style="position: absolute; top: -10px; right: -10px; width: 24px; height: 24px; background: #ef4444; border: 2px solid white; border-radius: 50%; cursor: pointer; display: none; align-items: center; justify-content: center; color: white; font-size: 14px; line-height: 1; z-index: 10; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">&times;</button>
  <h3 style="color: ${config.colors.border}; margin-top: 0;">${config.title}</h3>
  <ul style="list-style: none !important; padding: 0 !important;">
${itemsHTML}
  </ul>
</div>
<style data-for="${componentId}">
  .editor-content-preview #${componentId}:hover .component-delete-btn { display: flex !important; }
  #${componentId} ul { list-style-type: none !important; padding-left: 0 !important; }
  #${componentId} ul li { list-style: none !important; }
  #${componentId} ul li:before {
    content: '${iconEmoji}';
    position: absolute;
    left: 0;
    color: ${config.colors.icon};
  }
</style>`;
      } else {
        // Bullet mode
        const itemsHTML = config.items?.map((item) => `    <li style="margin-bottom: 10px; color: ${config.colors.text};">${item}</li>`).join('\n') || '';
        return `<div id="${componentId}" class="key-takeaways skillzen-component" style="background-color: ${config.colors.background}; border-left: 5px solid ${config.colors.border}; padding: 20px; margin-top: 40px; border-radius: 4px; position: relative;" data-component="key-takeaways" data-use-custom-icon="false" data-icon="${iconEmoji}" data-text-color="${config.colors.text}" data-icon-color="${config.colors.icon}">
  <button type="button" class="component-delete-btn" onclick="this.parentElement.remove()" title="Delete" style="position: absolute; top: -10px; right: -10px; width: 24px; height: 24px; background: #ef4444; border: 2px solid white; border-radius: 50%; cursor: pointer; display: none; align-items: center; justify-content: center; color: white; font-size: 14px; line-height: 1; z-index: 10; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">&times;</button>
  <h3 style="color: ${config.colors.border}; margin-top: 0;">${config.title}</h3>
  <ul style="list-style-type: disc; padding-left: 20px; color: ${config.colors.icon};">
${itemsHTML}
  </ul>
</div>
<style data-for="${componentId}">
  .editor-content-preview #${componentId}:hover .component-delete-btn { display: flex !important; }
  #${componentId} ul li:before { content: none !important; }
</style>`;
      }
    }

    if (type === 'example-box') {
      return `<div id="${componentId}" class="example-box skillzen-component" style="background-color: ${config.colors.background}; border: 2px solid ${config.colors.border}; padding: 20px; margin: 25px 0; border-radius: 8px; position: relative;" data-component="example-box" data-icon="${config.icon}" data-text-color="${config.colors.text}" data-icon-color="${config.colors.icon}">
  <button type="button" class="component-delete-btn" onclick="this.parentElement.remove()" title="Delete" style="position: absolute; top: -10px; right: -10px; width: 24px; height: 24px; background: #ef4444; border: 2px solid white; border-radius: 50%; cursor: pointer; display: none; align-items: center; justify-content: center; color: white; font-size: 14px; line-height: 1; z-index: 10; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">&times;</button>
  <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 12px;">
    <span style="font-size: 1.5em;">${config.icon}</span>
    <h3 style="color: ${config.colors.border}; margin: 0; font-size: 1.1em; font-weight: 600;">${config.title}</h3>
  </div>
  <div style="color: ${config.colors.text}; line-height: 1.6; padding-left: 5px;">${config.content}</div>
</div>
<style data-for="${componentId}">
  .editor-content-preview #${componentId}:hover .component-delete-btn { display: flex !important; }
</style>`;
    }

    if (type.startsWith('callout-')) {
      const calloutClass = type.replace('callout-', '');
      return `<div id="${componentId}" class="callout callout-${calloutClass} skillzen-component" style="background-color: ${config.colors.background}; border-left: 4px solid ${config.colors.border}; padding: 16px 20px; margin: 20px 0; border-radius: 0 8px 8px 0; position: relative; display: flex; gap: 12px;" data-component="${type}" data-icon="${config.icon}" data-text-color="${config.colors.text}" data-icon-color="${config.colors.icon}">
  <button type="button" class="component-delete-btn" onclick="this.parentElement.remove()" title="Delete" style="position: absolute; top: -10px; right: -10px; width: 24px; height: 24px; background: #ef4444; border: 2px solid white; border-radius: 50%; cursor: pointer; display: none; align-items: center; justify-content: center; color: white; font-size: 14px; line-height: 1; z-index: 10; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">&times;</button>
  <div style="flex-shrink: 0; font-size: 1.4em; line-height: 1;">${config.icon}</div>
  <div style="flex: 1;">
    <div style="font-weight: 600; color: ${config.colors.border}; margin-bottom: 6px; font-size: 0.95em;">${config.title}</div>
    <div style="color: ${config.colors.text}; line-height: 1.5; font-size: 0.95em;">${config.content}</div>
  </div>
</div>
<style data-for="${componentId}">
  .editor-content-preview #${componentId}:hover .component-delete-btn { display: flex !important; }
</style>`;
    }

    return '';
  };

  const handleInsert = () => {
    const html = generateHTML();
    onInsert(html);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-[3vh]">
      <div className="bg-white w-full h-full rounded-xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header - Compact */}
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-brand-blue-500 to-brand-indigo-600 rounded-lg flex items-center justify-center text-xl shadow">
              {info.icon}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {editingElement ? 'Edit' : 'Insert'} {info.title.replace('Configure ', '')}
              </h2>
              <p className="text-xs text-gray-500">
                {editingElement ? 'Modify existing component' : info.description}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors group"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-400 group-hover:text-gray-700" />
          </button>
        </div>

        {/* Content - Clean Layout */}
        <div className="flex-1 overflow-y-auto p-5 bg-gradient-to-br from-slate-50 to-brand-blue-50">
          <div className="max-w-7xl mx-auto h-full">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 h-full">
            {/* Configuration Panel - 3 columns */}
            <div className="lg:col-span-3 space-y-4">
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-base font-bold mb-4 text-gray-900 flex items-center gap-2">
                  <div className="w-6 h-6 bg-brand-blue-100 rounded flex items-center justify-center text-sm">
                    ⚙️
                  </div>
                  Configuration
                </h3>

                {/* Title & Icon Row */}
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={config.title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                    />
                  </div>
                  {/* Show icon picker for components other than objectives/takeaways (they have separate list style toggle) */}
                  {type !== 'learning-objectives' && type !== 'key-takeaways' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Icon</label>
                      <div className="flex gap-1">
                        {ICON_OPTIONS[type].map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => setConfig({ ...config, icon: emoji })}
                            className={`w-8 h-8 text-lg rounded border transition-all ${
                              config.icon === emoji
                                ? 'border-brand-blue-500 bg-brand-blue-50 shadow-sm'
                                : 'border-gray-300 bg-white hover:border-brand-blue-300'
                            }`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* List Style Toggle (for objectives and takeaways) */}
                {(type === 'learning-objectives' || type === 'key-takeaways') && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">List Style</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setConfig({ ...config, useCustomIcon: false })}
                        className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                          !config.useCustomIcon
                            ? 'border-brand-blue-500 bg-brand-blue-50 text-brand-blue-700'
                            : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <span className="text-lg">•</span>
                        Bullets
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfig({ ...config, useCustomIcon: true })}
                        className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                          config.useCustomIcon
                            ? 'border-brand-blue-500 bg-brand-blue-50 text-brand-blue-700'
                            : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <span className="text-lg">{config.icon}</span>
                        Custom Icon
                      </button>
                    </div>
                  </div>
                )}

                {/* Icon Selection (only show when custom icon is selected) */}
                {(type === 'learning-objectives' || type === 'key-takeaways') && config.useCustomIcon && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Custom Icon</label>
                    <div className="flex gap-1 flex-wrap">
                      {ICON_OPTIONS[type].map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => setConfig({ ...config, icon: emoji })}
                          className={`w-9 h-9 text-lg rounded border transition-all ${
                            config.icon === emoji
                              ? 'border-brand-blue-500 bg-brand-blue-50 shadow-sm'
                              : 'border-gray-300 bg-white hover:border-brand-blue-300'
                          }`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Items (for objectives and takeaways) */}
                {(type === 'learning-objectives' || type === 'key-takeaways') && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Items {config.items && `(${config.items.length})`}
                    </label>
                    <div className="space-y-2">
                      {config.items?.map((item, index) => (
                        <div key={index} className="flex gap-2">
                          <div className="flex-shrink-0 w-6 h-8 flex items-center justify-center bg-brand-blue-100 text-brand-blue-600 text-xs font-bold rounded">
                            {index + 1}
                          </div>
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => handleItemChange(index, e.target.value)}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 text-sm"
                            placeholder={`Item ${index + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddItem}
                      className="mt-2 px-3 py-1.5 text-sm bg-brand-blue-500 text-white rounded-lg hover:bg-brand-blue-600 transition-colors flex items-center gap-1 font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>
                )}

                {/* Question & Answer (for knowledge check) */}
                {type === 'knowledge-check' && (
                  <>
                    <div className="mb-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">❓ Question</label>
                      <textarea
                        value={config.question}
                        onChange={(e) => setConfig({ ...config, question: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                        rows={2}
                        placeholder="Enter your question here..."
                      />
                    </div>
                    <div className="mb-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">✅ Answer</label>
                      <textarea
                        value={config.answer}
                        onChange={(e) => setConfig({ ...config, answer: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                        rows={3}
                        placeholder="Enter the answer here..."
                      />
                    </div>
                    <div className="mb-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        💡 Going Deeper (Optional)
                      </label>
                      <textarea
                        value={config.extension}
                        onChange={(e) => setConfig({ ...config, extension: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                        rows={2}
                        placeholder="Additional insight or context..."
                      />
                    </div>

                    {/* Font Size Controls */}
                    <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">📏 Font Sizes</label>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Title</label>
                          <select
                            value={config.fontSize?.title || '1.1em'}
                            onChange={(e) => setConfig({
                              ...config,
                              fontSize: { ...config.fontSize!, title: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                          >
                            <option value="0.9em">Small</option>
                            <option value="1em">Medium</option>
                            <option value="1.1em">Large</option>
                            <option value="1.25em">X-Large</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Question</label>
                          <select
                            value={config.fontSize?.question || '1em'}
                            onChange={(e) => setConfig({
                              ...config,
                              fontSize: { ...config.fontSize!, question: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                          >
                            <option value="0.85em">Small</option>
                            <option value="0.95em">Medium</option>
                            <option value="1em">Large</option>
                            <option value="1.1em">X-Large</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Answer</label>
                          <select
                            value={config.fontSize?.answer || '0.95em'}
                            onChange={(e) => setConfig({
                              ...config,
                              fontSize: { ...config.fontSize!, answer: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                          >
                            <option value="0.85em">Small</option>
                            <option value="0.95em">Medium</option>
                            <option value="1em">Large</option>
                            <option value="1.1em">X-Large</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Container Style Controls */}
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">📦 Container Style</label>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Border Style</label>
                          <select
                            value={config.style?.borderStyle || 'solid'}
                            onChange={(e) => setConfig({
                              ...config,
                              style: { ...config.style!, borderStyle: e.target.value as 'solid' | 'dashed' | 'dotted' | 'none' }
                            })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                          >
                            <option value="solid">Solid</option>
                            <option value="dashed">Dashed</option>
                            <option value="dotted">Dotted</option>
                            <option value="none">None</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Border Width</label>
                          <select
                            value={config.style?.borderWidth || '1px'}
                            onChange={(e) => setConfig({
                              ...config,
                              style: { ...config.style!, borderWidth: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                          >
                            <option value="1px">Thin (1px)</option>
                            <option value="2px">Medium (2px)</option>
                            <option value="3px">Thick (3px)</option>
                            <option value="4px">Extra Thick (4px)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Border Radius</label>
                          <select
                            value={config.style?.borderRadius || '8px'}
                            onChange={(e) => setConfig({
                              ...config,
                              style: { ...config.style!, borderRadius: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                          >
                            <option value="0px">None</option>
                            <option value="4px">Small</option>
                            <option value="8px">Medium</option>
                            <option value="12px">Large</option>
                            <option value="16px">X-Large</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Padding</label>
                          <select
                            value={config.style?.padding || '20px'}
                            onChange={(e) => setConfig({
                              ...config,
                              style: { ...config.style!, padding: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                          >
                            <option value="12px">Compact</option>
                            <option value="16px">Small</option>
                            <option value="20px">Medium</option>
                            <option value="24px">Large</option>
                            <option value="32px">Spacious</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Shadow</label>
                        <div className="flex gap-2">
                          {(['none', 'sm', 'md', 'lg'] as const).map((shadow) => (
                            <button
                              key={shadow}
                              type="button"
                              onClick={() => setConfig({
                                ...config,
                                style: { ...config.style!, shadow }
                              })}
                              className={`flex-1 px-2 py-1.5 text-xs rounded-md border transition-all ${
                                config.style?.shadow === shadow
                                  ? 'border-brand-blue-500 bg-brand-blue-100 text-brand-blue-700'
                                  : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                              }`}
                            >
                              {shadow === 'none' ? 'None' : shadow.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Button Style Controls */}
                    <div className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">🔘 Button Style</label>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Button Text</label>
                          <input
                            type="text"
                            value={config.style?.buttonText || 'Show Answer'}
                            onChange={(e) => setConfig({
                              ...config,
                              style: { ...config.style!, buttonText: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                            placeholder="Show Answer"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Button Shape</label>
                          <select
                            value={config.style?.buttonStyle || 'rounded'}
                            onChange={(e) => setConfig({
                              ...config,
                              style: { ...config.style!, buttonStyle: e.target.value as 'rounded' | 'pill' | 'square' }
                            })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                          >
                            <option value="rounded">Rounded</option>
                            <option value="pill">Pill</option>
                            <option value="square">Square</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Button Background</label>
                          <input
                            type="color"
                            value={config.colors.buttonBg || '#3b82f6'}
                            onChange={(e) => setConfig({
                              ...config,
                              colors: { ...config.colors, buttonBg: e.target.value }
                            })}
                            className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Button Text Color</label>
                          <input
                            type="color"
                            value={config.colors.buttonText || '#ffffff'}
                            onChange={(e) => setConfig({
                              ...config,
                              colors: { ...config.colors, buttonText: e.target.value }
                            })}
                            className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Answer Box Style Controls */}
                    <div className="mb-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">💬 Answer Box Style</label>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Answer Border Style</label>
                          <select
                            value={config.style?.answerBorderStyle || 'solid'}
                            onChange={(e) => setConfig({
                              ...config,
                              style: { ...config.style!, answerBorderStyle: e.target.value as 'solid' | 'dashed' | 'gradient' }
                            })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                          >
                            <option value="solid">Solid</option>
                            <option value="dashed">Dashed</option>
                            <option value="gradient">Gradient</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Answer Background</label>
                          <input
                            type="color"
                            value={config.colors.answerBg || '#ffffff'}
                            onChange={(e) => setConfig({
                              ...config,
                              colors: { ...config.colors, answerBg: e.target.value }
                            })}
                            className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.style?.showTitleIcon !== false}
                            onChange={(e) => setConfig({
                              ...config,
                              style: { ...config.style!, showTitleIcon: e.target.checked }
                            })}
                            className="rounded border-gray-300 text-brand-blue-500 focus:ring-brand-blue-500"
                          />
                          Show title icon
                        </label>
                        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.style?.showAnswerLabel !== false}
                            onChange={(e) => setConfig({
                              ...config,
                              style: { ...config.style!, showAnswerLabel: e.target.checked }
                            })}
                            className="rounded border-gray-300 text-brand-blue-500 focus:ring-brand-blue-500"
                          />
                          Show "Answer:" label
                        </label>
                        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.style?.showExtensionLabel !== false}
                            onChange={(e) => setConfig({
                              ...config,
                              style: { ...config.style!, showExtensionLabel: e.target.checked }
                            })}
                            className="rounded border-gray-300 text-brand-blue-500 focus:ring-brand-blue-500"
                          />
                          Show "Going Deeper:" label
                        </label>
                      </div>
                    </div>
                  </>
                )}

                {/* Content (for example-box and callouts) */}
                {(type === 'example-box' || type.startsWith('callout-')) && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {type === 'example-box' ? '📝 Example Content' : '📄 Content'}
                    </label>
                    <textarea
                      value={config.content}
                      onChange={(e) => setConfig({ ...config, content: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500"
                      rows={5}
                      placeholder={
                        type === 'example-box'
                          ? 'Enter your example content here. This could include code, scenarios, or demonstrations...'
                          : 'Enter your callout content here...'
                      }
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {type === 'example-box'
                        ? 'Tip: Use examples that directly relate to the concepts being taught'
                        : type === 'callout-tip'
                        ? 'Tip: Share actionable advice that helps learners succeed'
                        : type === 'callout-warning'
                        ? 'Warning: Highlight potential pitfalls or common mistakes'
                        : type === 'callout-note'
                        ? 'Note: Add important reminders learners should keep in mind'
                        : 'Info: Share interesting facts or supplementary information'}
                    </p>
                  </div>
                )}

                {/* Color Configuration */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-bold mb-3 text-gray-900 flex items-center gap-2">
                    <Palette className="w-4 h-4 text-brand-indigo-600" />
                    Colors
                  </h4>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Background</label>
                      <input
                        type="color"
                        value={config.colors.background}
                        onChange={(e) => handleColorChange('background', e.target.value)}
                        className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Border</label>
                      <input
                        type="color"
                        value={config.colors.border}
                        onChange={(e) => handleColorChange('border', e.target.value)}
                        className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Text</label>
                      <input
                        type="color"
                        value={config.colors.text}
                        onChange={(e) => handleColorChange('text', e.target.value)}
                        className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Icon</label>
                      <input
                        type="color"
                        value={config.colors.icon}
                        onChange={(e) => handleColorChange('icon', e.target.value)}
                        className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Panel - 2 columns */}
            <div className="lg:col-span-2">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 sticky top-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-green-600" />
                    Live Preview
                  </h3>
                  {type === 'knowledge-check' && (
                    <button
                      type="button"
                      onClick={() => setShowAnswerPreview(!showAnswerPreview)}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                        showAnswerPreview
                          ? 'bg-green-100 text-green-700 border border-green-300'
                          : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {showAnswerPreview ? '✓ Answer Visible' : 'Show Answer'}
                    </button>
                  )}
                </div>
                <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gradient-to-br from-white to-gray-50 max-h-[60vh] overflow-auto preview-container">
                  <style>{`.preview-container .component-delete-btn { display: none !important; }`}</style>
                  {type === 'knowledge-check' ? (
                    (() => {
                      const style = config.style || {
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderRadius: '8px',
                        padding: '20px',
                        shadow: 'sm',
                        buttonStyle: 'rounded',
                        buttonText: 'Show Answer',
                        showAnswerLabel: true,
                        showExtensionLabel: true,
                        answerBorderStyle: 'solid',
                      };
                      const shadowStyles: Record<string, string> = {
                        none: 'none',
                        sm: '0 1px 2px rgba(0,0,0,0.05)',
                        md: '0 4px 6px rgba(0,0,0,0.1)',
                        lg: '0 10px 15px rgba(0,0,0,0.1)',
                      };
                      const buttonRadius: Record<string, string> = {
                        rounded: '6px',
                        pill: '50px',
                        square: '0px',
                      };
                      const answerBorderStyles: Record<string, string> = {
                        solid: `4px solid ${config.colors.icon}`,
                        dashed: `4px dashed ${config.colors.icon}`,
                        gradient: `4px solid ${config.colors.icon}`,
                      };
                      return (
                        <div
                          style={{
                            backgroundColor: config.colors.background,
                            border: `${style.borderWidth} ${style.borderStyle} ${config.colors.border}`,
                            padding: style.padding,
                            borderRadius: style.borderRadius,
                            boxShadow: shadowStyles[style.shadow],
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 'bold',
                              color: config.colors.border,
                              marginBottom: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: config.fontSize?.title || '1.1em',
                            }}
                          >
                            {style.showTitleIcon && (
                              <span style={{ marginRight: '10px', fontSize: '1.3em' }}>{config.icon}</span>
                            )}
                            {config.title}
                          </div>
                          <div
                            style={{
                              fontWeight: 600,
                              marginBottom: '16px',
                              color: config.colors.text,
                              fontSize: config.fontSize?.question || '1em',
                              lineHeight: 1.5,
                            }}
                          >
                            {config.question}
                          </div>
                          {showAnswerPreview && (
                            <div
                              style={{
                                backgroundColor: config.colors.answerBg || '#ffffff',
                                borderLeft: answerBorderStyles[style.answerBorderStyle],
                                padding: '16px',
                                marginBottom: '16px',
                                borderRadius: '6px',
                                color: config.colors.text,
                                fontSize: config.fontSize?.answer || '0.95em',
                                lineHeight: 1.6,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              }}
                            >
                              {style.showAnswerLabel && (
                                <strong style={{ color: config.colors.icon, fontSize: '0.9em', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  Answer:
                                </strong>
                              )}
                              <div style={{ marginTop: style.showAnswerLabel ? '8px' : '0' }}>{config.answer}</div>
                              {config.extension && (
                                <div
                                  style={{
                                    marginTop: '14px',
                                    paddingTop: '14px',
                                    borderTop: '1px dashed #d1d5db',
                                    fontStyle: 'italic',
                                    color: '#4b5563',
                                  }}
                                >
                                  {style.showExtensionLabel && (
                                    <span style={{ color: config.colors.icon, fontWeight: 600 }}>💡 Going Deeper: </span>
                                  )}
                                  {config.extension}
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => setShowAnswerPreview(!showAnswerPreview)}
                            style={{
                              backgroundColor: config.colors.buttonBg || config.colors.icon,
                              color: config.colors.buttonText || 'white',
                              border: 'none',
                              padding: '12px 24px',
                              borderRadius: buttonRadius[style.buttonStyle],
                              cursor: 'pointer',
                              fontSize: '0.95em',
                              fontWeight: 600,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          >
                            {showAnswerPreview ? 'Hide Answer' : style.buttonText}
                          </button>
                        </div>
                      );
                    })()
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: generateHTML() }} />
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Footer - Compact Action Bar */}
        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-end gap-3 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleInsert}
            className="px-5 py-2 text-sm bg-gradient-to-r from-brand-blue-600 to-brand-indigo-600 text-white rounded-lg hover:from-brand-blue-700 hover:to-brand-indigo-700 transition-all font-semibold flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {editingElement ? 'Update' : 'Insert'}
          </button>
        </div>
      </div>
    </div>
  );
}
